/**
 * Rewrites hard-coded colours in a stylesheet into theme tokens.
 *
 * Used twice: as a one-off codemod over the stylesheets on disk
 * (client/scripts/tokenise-colours.mjs), and on every import for the per-page
 * CSS the landing pages carry in MongoDB. Both need the identical mapping, or
 * a landing page stops following the theme the rest of the site is wearing.
 *
 * The mapping is per-property on purpose. The same colour does two jobs:
 * #C88A53 is a background 34 times and text 29 times, and the token that suits
 * one is wrong for the other.
 */

import { contrastRatio, luminance, parseColor, THRESHOLD } from './contrast.js';

export type ColourRole = 'text' | 'bg' | 'border';

const MAP: Record<string, Record<ColourRole, string | null>> = {
  // espresso / browns
  '#4A2C2A': { text: '--t-ink', bg: '--t-inverse', border: '--t-border' },
  '#3E2220': { text: '--t-ink', bg: '--t-inverse', border: '--t-border' },
  '#5C3330': { text: '--t-ink', bg: '--t-inverse', border: '--t-border' },
  '#2E1A11': { text: '--t-ink-strong', bg: '--t-inverse', border: '--t-border' },
  '#2C1B14': { text: '--t-ink-strong', bg: '--t-inverse', border: '--t-border' },
  '#482618': { text: '--t-ink-strong', bg: '--t-inverse', border: '--t-border' },
  '#4A2512': { text: '--t-ink-strong', bg: '--t-inverse', border: '--t-border' },
  '#1A0F0D': { text: '--t-ink-strong', bg: '--t-inverse', border: '--t-border' },
  '#1A1A1A': { text: '--t-ink-strong', bg: '--t-inverse', border: '--t-border' },
  '#6D4C41': { text: '--t-ink-soft', bg: '--t-inverse', border: '--t-border' },
  '#9C7B70': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '#7A6F6A': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '#685E5A': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '#886A68': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '#666666': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '#555555': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '#333333': { text: '--t-ink', bg: '--t-inverse', border: '--t-border' },

  // accent
  '#C88A53': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '#C69320': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '#B8860B': { text: '--t-accent-ink', bg: '--t-accent-deep', border: '--t-accent' },
  '#9A6328': { text: '--t-accent-ink', bg: '--t-accent-deep', border: '--t-accent' },
  '#8B4513': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '#CFAA86': { text: '--t-accent-ink', bg: '--t-accent-soft', border: '--t-border' },
  '#E5C595': { text: '--t-on-inverse', bg: '--t-accent-soft', border: '--t-border' },
  '#F9D575': { text: '--t-on-inverse', bg: '--t-accent-soft', border: '--t-border' },
  '#F7ECD8': { text: '--t-ink', bg: '--t-accent-soft', border: '--t-border' },
  '#F3E7D8': { text: '--t-ink', bg: '--t-accent-soft', border: '--t-border' },
  '#FDECE2': { text: '--t-ink', bg: '--t-accent-soft', border: '--t-border' },

  // surfaces
  '#FFF9F2': { text: '--t-on-inverse', bg: '--t-bg', border: '--t-border' },
  '#FDF5E6': { text: '--t-on-inverse', bg: '--t-bg-alt', border: '--t-border' },
  '#F5EAD4': { text: '--t-on-inverse', bg: '--t-bg-sunken', border: '--t-border' },
  '#FDFCF8': { text: '--t-on-inverse', bg: '--t-surface', border: '--t-border' },
  '#FBF8F4': { text: '--t-on-inverse', bg: '--t-surface-alt', border: '--t-border' },
  '#FCFBF9': { text: '--t-on-inverse', bg: '--t-surface-alt', border: '--t-border' },
  '#F4F1EA': { text: '--t-on-inverse', bg: '--t-surface-alt', border: '--t-border' },
  '#FFFFFF': { text: '--t-on-inverse', bg: '--t-surface', border: '--t-border' },

  // lines
  '#E8E4DC': { text: null, bg: '--t-bg-sunken', border: '--t-border' },
  '#EBE5DC': { text: null, bg: '--t-bg-sunken', border: '--t-border' },
  '#E6DCDA': { text: null, bg: '--t-bg-sunken', border: '--t-border' },

  // the migrated price tables
  '#A66E4E': { text: '--t-accent-ink', bg: '--t-accent-soft', border: '--t-border' },
  '#9C8070': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '#3A1E0E': { text: '--t-ink-strong', bg: '--t-inverse', border: '--t-border' },
  '#C8B0A0': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '#FAF6F2': { text: '--t-on-inverse', bg: '--t-bg-alt', border: '--t-border' },
  '#F5F0EA': { text: '--t-on-inverse', bg: '--t-bg-alt', border: '--t-border' },
  '#FFF8F0': { text: '--t-on-inverse', bg: '--t-bg-alt', border: '--t-border' },
  '#D4AF37': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '#C5A059': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '#B85C10': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '#D4A017': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '#B0A6A0': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },

  // Greys used as body copy on the cream canvas. All three measured between
  // 1.8:1 and 4.3:1 there; --t-ink-muted is the token that means "quieter copy"
  // and clears AA at 6.6:1.
  '#B8B8B8': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '#777777': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '#CCCCCC': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },

  // Bright accents that only work as fills. As words on white they run from
  // 1.4:1 (the yellow stars) to 3.3:1, so as text they take the accent ink.
  '#FDD835': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '#E26A2C': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '#F7903A': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '#D75425': { text: '--t-accent-ink', bg: '--t-accent-deep', border: '--t-accent' },

  // status
  '#27AE60': { text: '--t-success', bg: '--t-success', border: '--t-success' },
  '#6FCF97': { text: '--t-success', bg: '--t-success', border: '--t-success' },
  '#C0392B': { text: '--t-danger', bg: '--t-danger', border: '--t-danger' },
  '#A03225': { text: '--t-danger', bg: '--t-danger', border: '--t-danger' },
  '#B3261E': { text: '--t-danger', bg: '--t-danger', border: '--t-danger' },
};
/** rgb() triples for the mapped colours, so rgba() can be rewritten too. */
const BY_RGB = new Map<string, Record<ColourRole, string | null>>();
for (const [hex, roles] of Object.entries(MAP)) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  BY_RGB.set(`${r},${g},${b}`, roles);
}

/**
 * Inherited tokens that mean two different things depending on the property.
 *
 * `--color-brown` is a dark band behind ten sections and the colour of four
 * pieces of text. An alias can only pick one, and picking wrong is invisible in
 * the palette the site shipped with — a dark brown reads fine as text on cream
 * *and* as a band. Switch to a dark palette and the text disappears, because
 * the "dark band" colour is now nearly the page colour.
 *
 * So the ambiguous ones are resolved here, where the property is known.
 */
const VAR_MAP: Record<string, Record<ColourRole, string>> = {
  // dark bands that are also used as text
  '--color-brown': { text: '--t-ink', bg: '--t-inverse', border: '--t-border' },
  '--color-brown-deep': { text: '--t-ink-strong', bg: '--t-inverse', border: '--t-border' },
  '--color-brown-espresso': { text: '--t-ink-strong', bg: '--t-inverse', border: '--t-border' },
  '--color-brown-dark': { text: '--t-ink', bg: '--t-inverse', border: '--t-border' },
  '--brown-deep': { text: '--t-ink-strong', bg: '--t-inverse', border: '--t-border' },
  '--bg-brown': { text: '--t-ink-strong', bg: '--t-inverse', border: '--t-border' },
  '--brown': { text: '--t-ink', bg: '--t-inverse', border: '--t-border' },
  '--brand-black': { text: '--t-ink-strong', bg: '--t-inverse', border: '--t-border' },
  '--color-brand-black': { text: '--t-ink-strong', bg: '--t-inverse', border: '--t-border' },

  // white is a card surface, and it is also the text on dark bands
  '--color-white': { text: '--t-on-inverse', bg: '--t-surface', border: '--t-border' },
  '--white': { text: '--t-on-inverse', bg: '--t-surface', border: '--t-border' },

  // the accent: legible as a word only in its darker form
  '--color-brand-gold': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '--gold': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '--primary-gold': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '--brand-yellow': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '--gold-btn': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '--accent-brown': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '--green': { text: '--t-success', bg: '--t-success', border: '--t-success' },
  '--separator-color': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '--gold-accent': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '--brand-gold': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },
  '--color-brand-gold-solid': { text: '--t-accent-ink', bg: '--t-accent', border: '--t-accent' },

  // the pale gold: text on dark bands, a wash behind things on light ones
  '--color-brand-gold-light': { text: '--t-on-inverse', bg: '--t-accent-soft', border: '--t-border' },
  '--gold-lt': { text: '--t-on-inverse', bg: '--t-accent-soft', border: '--t-border' },
  '--gold-light': { text: '--t-on-inverse', bg: '--t-accent-soft', border: '--t-border' },

  // The mid-tone browns. Overwhelmingly text — secondary copy and captions —
  // but they carry a colour name rather than a role name, so nothing about
  // `--brown-soft` says "this is what a caption is painted with".
  '--brown-soft': { text: '--t-ink-soft', bg: '--t-bg-sunken', border: '--t-border' },
  '--brown-muted': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '--brown-mid': { text: '--t-ink-soft', bg: '--t-bg-sunken', border: '--t-border' },
  '--color-brown-soft': { text: '--t-ink-soft', bg: '--t-bg-sunken', border: '--t-border' },
  '--color-brown-muted': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '--color-brown-mid': { text: '--t-ink-soft', bg: '--t-bg-sunken', border: '--t-border' },
  '--text-muted': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '--text-light': { text: '--t-ink-soft', bg: '--t-bg-sunken', border: '--t-border' },
  '--text-grey': { text: '--t-ink-muted', bg: '--t-bg-sunken', border: '--t-border' },
  '--text-dark': { text: '--t-ink', bg: '--t-inverse', border: '--t-border' },
  '--text-main': { text: '--t-ink', bg: '--t-inverse', border: '--t-border' },

  // accent tints
  '--gold-pale': { text: '--t-accent-ink', bg: '--t-accent-soft', border: '--t-border' },
  '--gold-deep': { text: '--t-accent-ink', bg: '--t-accent-deep', border: '--t-accent' },
  '--color-brand-gold-pale': { text: '--t-accent-ink', bg: '--t-accent-soft', border: '--t-border' },
  '--color-brand-gold-deep': { text: '--t-accent-ink', bg: '--t-accent-deep', border: '--t-accent' },

  // Contract tokens used in the wrong role. `--t-inverse` is the dark band; as
  // a text colour it means "the darkest ink", which is `--t-ink`. In a light
  // palette the two are near enough to be indistinguishable, which is exactly
  // why this went unnoticed until a dark palette existed.
  '--t-inverse': { text: '--t-ink', bg: '--t-inverse', border: '--t-border' },
  '--t-bg': { text: '--t-on-inverse', bg: '--t-bg', border: '--t-border' },
  '--t-surface': { text: '--t-on-inverse', bg: '--t-surface', border: '--t-border' },
};

/** Which job is this declaration doing? Shadows are left alone: they read the
 *  same in every palette, and tinting them tends to muddy a card. */
export function roleFor(property: string): ColourRole | null {
  const p = property.toLowerCase();

  /*
   * A component that declares its own alias — `--header-text: var(--t-inverse)`
   * — hides the job from the property name, so the name of the *variable* is
   * what has to be read. Getting this wrong is invisible in a light palette and
   * catastrophic in a dark one: the header's "deep brown text" becomes the
   * near-black that is now the page itself.
   *
   * Anything whose name does not say what it is for is left alone. A wrong
   * guess is worse than no change.
   */
  if (p.startsWith('--')) {
    if (/(^|-)(text|ink|fg|foreground|label|title|heading)(-|$)/.test(p)) return 'text';
    if (/(^|-)(bg|background|surface|fill|canvas)(-|$)/.test(p)) return 'bg';
    if (/(^|-)(border|line|divider|rule|outline|stroke)(-|$)/.test(p)) return 'border';
    return null;
  }

  if (p === 'color' || p === 'fill' || p === 'stroke' || p === '-webkit-text-fill-color') return 'text';
  if (p.includes('shadow')) return null;
  if (p.startsWith('border') || p === 'outline' || p === 'outline-color') return 'border';
  return 'bg';
}

export interface TokeniseResult { css: string; replaced: number }

/**
 * The default palette's values, so a rule can be checked rather than guessed.
 *
 * These are espresso-gold's, the preset every other one is validated against.
 * A palette swap moves the exact ratios, but not the pairing: the token that
 * belongs on a dark band is the on-inverse one in every palette, which is the
 * property this pass actually relies on.
 */
const PALETTE: Record<string, string> = {
  '--t-bg': '#FFF9F2',
  '--t-bg-alt': '#FDF5E6',
  '--t-bg-sunken': '#F5EAD4',
  '--t-surface': '#FFFFFF',
  '--t-surface-alt': '#FDF7EF',
  '--t-ink': '#3A211F',
  '--t-ink-strong': '#2A1512',
  '--t-ink-soft': '#5C3B34',
  '--t-ink-muted': '#6B554E',
  '--t-accent': '#8A5A22',
  '--t-on-accent': '#FFF8EE',
  '--t-accent-ink': '#7A4D18',
  '--t-accent-soft': '#F7ECD8',
  '--t-accent-deep': '#6B4415',
  '--t-inverse': '#2E1A11',
  '--t-on-inverse': '#F5E6D0',
  '--t-on-inverse-soft': '#D3BBA0',
  '--t-accent-on-inverse': '#E0B074',
  '--t-border': '#E0D5C6',
  '--t-success': '#1B7A45',
  '--t-on-success': '#FFF8EE',
  '--t-success-on-inverse': '#6FCF97',
  '--t-danger': '#A32B1E',
};

/** Resolves `var(--t-x)`, `#abc` or `rgb(...)` to a concrete colour. */
function resolveColour(value: string): string | null {
  // A gradient or an image has no single colour behind the text, and picking
  // its first stop is worse than declining: `linear-gradient(90deg, dark, ...)`
  // would be read as a flat dark fill and every light word on it "repaired"
  // into an ink that is invisible over the rest of the ramp.
  // color-mix() is the same problem wearing a different hat: the migrated
  // CSS uses it for tints - `color-mix(in srgb, var(--t-success) 18%,
  // transparent)` is a faint wash, not the token - and reading the first
  // var() inside it as the fill reports a solid green behind a badge that
  // is actually nearly clear.
  if (/gradient\(|url\(|image\(|color-mix\(/i.test(value)) return null;
  const token = value.match(/var\(\s*(--[a-zA-Z0-9-]+)\s*\)/);
  if (token) return PALETTE[token[1]!] ?? null;
  const literal = value.match(/#[0-9a-fA-F]{3,8}|rgba?\([^)]*\)/);
  if (!literal) return null;
  // A translucent fill sits on something unknown, so it cannot be judged here.
  const alpha = literal[0].match(/rgba\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*([\d.]+)/);
  if (alpha && Number(alpha[1]) < 0.95) return null;
  return literal[0];
}

/**
 * Foregrounds to reach for, in preference order, once a background is known.
 *
 * Ordered so the REPLACEMENT KEEPS THE INTENT of what it replaces: an accent
 * word stays an accent word, quiet copy stays quiet. Only the value moves.
 */
const ON_DARK = ['--t-on-inverse', '--t-on-inverse-soft'];
const ON_DARK_ACCENT = ['--t-accent-on-inverse', '--t-on-inverse'];
const ON_LIGHT = ['--t-ink', '--t-ink-strong', '--t-ink-muted'];
const ON_LIGHT_ACCENT = ['--t-accent-ink', '--t-accent-deep', '--t-ink-strong'];
const ON_ACCENT = ['--t-on-accent', '--t-ink-strong'];
const ON_SUCCESS = ['--t-on-success', '--t-ink-strong'];

/** Is this foreground token an accent word, as opposed to plain copy? */
const isAccentFg = (v: string): boolean => /--t-accent(?:-ink|-deep|-on-inverse)?\)/.test(v);

/**
 * Second pass, over whole rules rather than single declarations.
 *
 * A declaration on its own cannot tell you what is behind it. `color: #FFF`
 * becomes `--t-on-inverse`, which is right on a dark band and wrong on a
 * button - and the palettes deliberately separate the two, because the colour
 * that reads on espresso is not the colour that reads on gold.
 *
 * When one rule sets both, the answer is knowable, so it is computed: the
 * pair is measured, and if it is under AA the foreground is swapped for the
 * first candidate of the same INTENT that clears 4.5:1 against that same
 * background. Nothing is changed when the pair already passes, and nothing is
 * changed when either side cannot be resolved to a colour - a guess there is
 * worse than leaving a human to look at it.
 *
 * This is where the badge pills were fixed: two dozen rules across the
 * migrated pages say `background: var(--t-inverse); color: var(--t-accent-ink)`,
 * which is the accent word token - correct on the page canvas, 2.3:1 on the
 * espresso fill the same rule sets.
 */
function pairForegrounds(css: string): { css: string; fixed: number } {
  let fixed = 0;
  const out = css.replace(/\{([^{}]*)\}/g, (whole, body: string) => {
    const bgDecl = body.match(/(?:^|;)\s*background(?:-color)?\s*:\s*([^;]+)/i)?.[1];
    if (!bgDecl) return whole;
    const bg = resolveColour(bgDecl);
    if (!bg) return whole;

    const next = body.replace(/(^|;)(\s*)color\s*:\s*([^;!]+)/gi,
      (decl, lead: string, space: string, value: string) => {
        const fg = resolveColour(value);
        if (!fg) return decl;
        if (contrastRatio(fg, bg) >= THRESHOLD.body) return decl;

        const dark = luminance(parseColor(bg)!) < 0.18;
        const accentish = isAccentFg(value);
        let candidates: string[];
        if (bgDecl.includes('var(--t-success)')) candidates = ON_SUCCESS;
        else if (/var\(--t-accent(-deep|-ink)?\)/.test(bgDecl)) candidates = ON_ACCENT;
        else if (dark) candidates = accentish ? ON_DARK_ACCENT : ON_DARK;
        else candidates = accentish ? ON_LIGHT_ACCENT : ON_LIGHT;

        const pick = candidates.find((t) => contrastRatio(PALETTE[t]!, bg) >= THRESHOLD.body);
        if (!pick) return decl;
        fixed += 1;
        // No space after the colon: the first pass normalises every other
        // declaration to `property:value`, and emitting a space here meant a
        // second run of the tokeniser produced a different string - a
        // migration that never reports itself finished.
        return `${lead}${space}color:var(${pick})`;
      });
    return `{${next}}`;
  });
  return { css: out, fixed };
}

export function tokeniseCss(css: string): TokeniseResult {
  let replaced = 0;
  const out = css.replace(/([-a-zA-Z]+)\s*:\s*([^;{}]+)/g, (whole, property: string, value: string) => {
    const role = roleFor(property);
    if (!role) return whole;
    let next = value;

    next = next.replace(/#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/g, (lit: string, digits: string) => {
      const hex = (digits.length === 3
        ? `#${digits.split('').map((c) => c + c).join('')}`
        : `#${digits}`).toUpperCase();
      const token = MAP[hex]?.[role];
      if (!token) return lit;
      replaced += 1;
      return `var(${token})`;
    });

    // Inherited tokens whose meaning depends on the property they are used in.
    next = next.replace(/var\(\s*(--[a-zA-Z0-9-]+)\s*\)/g, (lit: string, name: string) => {
      const token = VAR_MAP[name.toLowerCase()]?.[role];
      if (!token || token === name) return lit;
      replaced += 1;
      return `var(${token})`;
    });

    next = next.replace(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/g,
      (lit: string, r: string, g: string, b: string, a: string | undefined) => {
        const alpha = a === undefined ? 1 : Number(a);
        const token = BY_RGB.get(`${r},${g},${b}`)?.[role];
        if (!token) return lit;
        replaced += 1;
        if (alpha >= 0.999) return `var(${token})`;
        return `color-mix(in srgb, var(${token}) ${Math.round(alpha * 100)}%, transparent)`;
      });

    return `${property}:${next}`;
  });
  const paired = pairForegrounds(out);
  return { css: paired.css, replaced: replaced + paired.fixed };
}
